import { verifierChampsDEcran } from './validation-champs.js';
import { CHAMP_PIECES_JOINTES, champsDeSource, champsDeCondition, enTetesDeLaPorte, type SourceSimple } from '@taki/data-model';
import { champsNatifs, estCarteDeChamps, schemaDeFamille } from '@taki/data-model';
import { bindingsDe, champsDuJeu, triDe, type ChampDeclare, type ChampsDePorte } from '@taki/ui';
import { definirAppDeclaree, type AppDeclaree } from './app-declaree.js';
import { accesDuJeu } from './acces.js';
import { qualifierLesNatifs } from './natifs.js';
import { effetsCites, fenetresCitees, portesCanvas } from './noms-cites.js';
import { clefDeSurfaceDeVue } from './vues-de-fiche-donnees.js';
import { resoudreDeclaration, type DeclarationInstance } from './resolution.js';

/** Rejoue le montage de toutes les portes avant l'enregistrement, sans lire ni écrire de données. */
export function verifierInstance(app: AppDeclaree, instance: DeclarationInstance): void {
  const resolution = resoudreDeclaration(app, instance);
  if (resolution.raison) throw new Error(resolution.raison);
  const { champs, ecrans } = resolution.declaration;
  /* L'ecran que l'ecran generique montera : type par le socle (`natifs.ts`). */
  const ecran = qualifierLesNatifs(resolution.declaration.ecran);
  verifierChampsDEcran(champs, ecran);
  const effets = new Set(effetsCites(ecran).map((effet) => effet.nom));
  /* LES SURFACES QUE L'ECRAN MONTE : celles des portes Canvas, et les
     composants `vue:<id>` des vues Canvas qu'il decrit (niveaux Vue et Fiche).
     Sans eux, toute personnalisation d'une app qui livre une vue Canvas
     (Projets, Timesheet) etait refusee a l'enregistrement : « l'app ne fournit
     aucun composant canvas['vue:<id>'] ». */
  const canvas = new Set([...portesCanvas(ecran), ...(ecran.vuesCustom ?? []).map((vue) => clefDeSurfaceDeVue(vue.id))]);
  /* UN SCRIPT QUE L'APP N'A PAS ENCORE CODE NE REFUSE PAS L'ENREGISTREMENT : un
     smart bouton « Script » cree au Studio d'une org nomme un effet (ou une
     fenetre) que l'agent codera apres, et l'ecran le grise d'ici la (« Script
     « x » pas encore codé », `docs/socle/smart-boutons.md`). Le rejeu recoit donc
     un substitut inerte pour chaque nom cite et absent ; le MODELE, lui, garde
     son refus nomme (`definirAppDeclaree` direct). */
  const fenetres = new Set(fenetresCitees(ecran).map((fenetre) => fenetre.nom));
  const substituts = Object.fromEntries([...effets].filter((nom) => app.effets[nom] === undefined).map((nom) => [nom, () => {}]));
  const fenetresSubstituts = Object.fromEntries([...fenetres].filter((nom) => app.fenetres[nom] === undefined).map((nom) => [nom, () => null]));
  // Une instance peut retirer un usage, le code livré du modèle reste disponible.
  definirAppDeclaree({
    ...app,
    /* Une App Canvas n'expose aucune declaration (`declarationDEntree`) : ce
       rejeu ne vise que les apps a portes. */
    appCanvas: undefined,
    champs,
    ecrans,
    fenetres: { ...app.fenetres, ...fenetresSubstituts },
    effets: { ...Object.fromEntries(Object.entries(app.effets).filter(([id]) => effets.has(id))), ...substituts },
    canvas: Object.fromEntries(Object.entries(app.canvas).filter(([id]) => canvas.has(id))),
  });
  const arrivee = ecran.portes.find((p) => p.id === ecran.porteArrivee)?.jeu ?? ecran.portes[0]?.jeu;
  const champsParPorte = (id: string) => {
    const jeu = ecran.portes.find((p) => p.id === id)?.jeu ?? arrivee;
    return champsDuJeu(champs, jeu?.family ?? '', jeu?.type);
  };
  /* COMME `assemblage.ts` : `duJeu` donne les champs d'un jeu qu'AUCUNE porte de
     l'ecran ne montre. Une lecture (`lecture.via`) vers un tel jeu (Consultations
     lit l'espece d'un animal) etait refusee faute de ce crochet. */
  const champsDe: ChampsDePorte = Object.assign(champsParPorte, {
    duJeu: (family: string, type?: string): readonly ChampDeclare[] => champsDuJeu(champs, family, type),
  });
  for (const jeu of champs.jeux) champsNatifs(jeu.family);
  const lecture = accesDuJeu(null, ecran.colonnes ?? []);
  triDe(ecran, champs.jeux.flatMap((jeu) => jeu.champs));
  for (const porte of ecran.portes) {
    for (const lien of porte.liens ?? []) {
      const cible = ecran.portes.find((candidate) => candidate.id === lien.porte);
      const jeuLie = cible?.jeu ?? arrivee;
      if (!cible || !jeuLie) throw new Error(`Le lien « ${lien.id} » vise une porte sans jeu de données.`);
      /* LE SENS DIRECT : `liste` est un champ de la fiche OUVERTE (le jeu de la
         porte qui declare le lien), un lien a `plusieurs` qui designe le jeu de
         la porte liee. Meme refus, au montage, que la CI (`check-app-entities`). */
      if (lien.liste !== undefined) {
        const champ = champsDe(porte.id).find((candidat) => candidat.key === lien.liste);
        if (champ === undefined) {
          throw new Error(`Le lien « ${lien.id} » cite la liste « ${lien.liste} », absente du jeu de la porte « ${porte.label} ».`);
        }
        if (champ.type !== 'lien' || champ.plusieurs !== true) {
          throw new Error(`Le lien « ${lien.id} » cite la liste « ${lien.liste} », qui n'est pas un champ de liens multiples (« plusieurs »).`);
        }
        const vise = champ.reference;
        if (vise !== undefined && (vise.family !== jeuLie.family || (jeuLie.type !== undefined && vise.type !== undefined && vise.type !== jeuLie.type))) {
          throw new Error(`Le lien « ${lien.id} » cite la liste « ${lien.liste} », qui désigne « ${vise.family} » et non le jeu de la porte « ${cible.label} ».`);
        }
        continue;
      }
      if (lien.par === undefined) throw new Error(`Le lien « ${lien.id} » ne porte ni « par » ni « liste ».`);
      const natifsLies = schemaDeFamille(jeuLie.family).shape;
      if (!(lien.par in natifsLies) && !champsDe(cible.id).some((champ) => champ.key === lien.par)) {
        throw new Error(`Le lien « ${lien.id} » cite le champ « ${lien.par} », absent du jeu de la porte « ${cible.label} ».`);
      }
    }
    const jeu = porte.jeu ?? arrivee;
    if (jeu) {
      const natifs = schemaDeFamille(jeu.family).shape;
      const declares = new Set(champsDe(porte.id).map((champ) => champ.key));
      const verifierChamp = (nom: string): void => {
        const existe = nom.startsWith('socle:')
          ? nom.slice(6) in natifs
          : declares.has(nom) || nom in natifs;
        if (!existe) throw new Error(`La porte « ${porte.label} » cite le champ « ${nom} », absent de son jeu de données.`);
      };
      for (const filtre of porte.filtres?.autres ?? []) if (filtre.condition) champsDeCondition(filtre.condition).forEach(verifierChamp);
      if (porte.tranche) champsDeCondition(porte.tranche).forEach(verifierChamp);
      for (const { verrous = [] } of enTetesDeLaPorte(porte)) for (const verrou of verrous) champsDeCondition(verrou.si).forEach(verifierChamp);
      /* LA CONDITION D'UN SMART BOUTON : le champ virtuel `socle:piecesJointes` n'est pas un champ du jeu. */
      for (const action of porte.actions ?? []) if (action.si) champsDeCondition(action.si).filter((nom) => nom !== CHAMP_PIECES_JOINTES).forEach(verifierChamp);
      for (const binding of Object.values(porte.vb ?? ecran.vb)) {
        if (!binding) continue;
        for (const regle of binding.surbrillances ?? []) if (regle.si !== 'nouveau') champsDeCondition(regle.si).forEach(verifierChamp);
        for (const [clef, valeur] of Object.entries(binding)) {
          if (clef === 'colonnes') (valeur as string[]).forEach(verifierChamp);
          else if (valeur && typeof valeur === 'object' && !Array.isArray(valeur)) {
            const source = valeur as SourceSimple;
            champsDeSource(source).forEach(verifierChamp);
          }
        }
        for (const carte of binding.cartes ?? []) {
          /* Une carte liee ou celle des fichiers ne cite aucun champ de CE jeu. */
          if (!estCarteDeChamps(carte)) continue;
          for (const champ of carte.champs) {
            if (typeof champ === 'string') verifierChamp(champ);
            else if (!('total' in champ)) verifierChamp(champ.champ);
          }
        }
      }
    }
    bindingsDe(ecran, champsDe(porte.id), lecture, app.fonctions, porte.id, champsDe);
  }
}
